import { Injectable } from '@angular/core';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpResponse, HttpErrorResponse, HttpHeaderResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/do';
import { CookieService } from 'ngx-cookie-service';
import { throwError, BehaviorSubject, of } from 'rxjs';
import { HttpSentEvent, HttpProgressEvent, HttpUserEvent } from '@angular/common/http';
import { AuthService } from './shared/services/auth.service';
import { InteractorService } from './core/services/interactor.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ErrorLogService } from './shared/services/error-log.service';
import { GlobalErrorHandlerService } from './shared/services/global-error-handler.service'
import { localStorageVariables } from 'src/app/core/localStorageConstants/localStorageConstants';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { ActivatedRoute } from '@angular/router';
import { LookupService } from './core/services/lookup.service';
import { Location } from '@angular/common';
import { EnvService } from './core/services/env.service';
import { TranslateService } from '@ngx-translate/core';

export interface ICurrentUser {
  accessToken: string;
  refreshToken: string;
}
@Injectable()
export class AppInterceptor implements HttpInterceptor {
  public loaderCount: any = 0;
  public isOverlayActive: boolean = false
  public emailAssessmentLang: any
  // Subscribed once below, not per-request: intercept() runs on every HTTP call, and
  // subscribing to these shared observables there (with no unsubscribe) added one more live
  // subscription per request for the life of the tab - O(N) stale callbacks firing on every
  // subsequent state change, for N = requests made so far this session (PCIS-11524).
  private overlaySubscribed = false

  constructor(
    public cookieService: CookieService,
    public authService: AuthService,
    public interactorService: InteractorService,
    private spinner: NgxSpinnerService,
    private errorLogService: ErrorLogService,
    private toastr: ToastrService,
    private localStorageService: LocalStorageService,
    private activatedRoute: ActivatedRoute,
    private lookupservice: LookupService,
    private globalErrorHandlerService: GlobalErrorHandlerService,
    private location: Location,
    private envService: EnvService,
    private translatorService: TranslateService,

  ) {
  }
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private readonly httpStatusExcludedUrls: string[] = [];

  /** Returns true when the request URL matches an entry in the exclusion list. */
  private isExcludedFromHttpStatusHandling(url: string): boolean {
    return this.httpStatusExcludedUrls.some(segment => url.includes(segment));
  }

  // function which will be called for all http calls
  intercept(request: HttpRequest<any>, next: HttpHandler):
    Observable<
      | HttpSentEvent
      | HttpHeaderResponse
      | HttpProgressEvent
      | HttpResponse<any>
      | HttpUserEvent<any>
      | any
    > {
    this.spinner.show();
    this.loaderCount++;
    if (!this.overlaySubscribed) {
      this.overlaySubscribed = true;
      this.lookupservice.showOverlay.subscribe((value: boolean) => {
        this.isOverlayActive = value ? true : false;
      });
      this.lookupservice.emailLanguageSubject.subscribe(value => {
        this.emailAssessmentLang = value;
      });
    }
    if (this.loaderCount > 0 && !this.isOverlayActive) {
      this.lookupservice.updateOverlayState(true);
    }

    // Check if this is a static asset request - skip auth and API headers for assets
    const isAssetRequest = request.url.includes('/assets/') || request.url.startsWith('assets/');
    if (isAssetRequest) {
      // For asset requests, only set cache-friendly headers
      request = request.clone({
        headers: request.headers.set('Cache-Control', 'public, max-age=86400')
      });
      return next.handle(request).pipe(
        tap((evt) => {
          if (evt instanceof HttpResponse) {
            this.loaderCount--;
            if (this.loaderCount <= 0) {
              this.spinner.hide();
              this.lookupservice.updateOverlayState(false);
            }
          }
        }),
        catchError((err) => {
          this.loaderCount--;
          if (this.loaderCount <= 0) {
            this.spinner.hide();
            this.lookupservice.updateOverlayState(false);
          }
          return throwError(err);
        })
      );
    }

    // const token: string = 'Bearer ' + this.cookieService.get('token');
    let token: string = this.localStorageService.getDataFromLocalStorage(localStorageVariables.token);
    if (token) {
      token = 'Bearer ' + token;
      request = request.clone({
        headers: request.headers
          .set('Authorization', token)
          .set('X-XSS-Protection', '1; mode=block')
          .set('Cache-Control', 'no-store')
          .set('Pragma', 'no-cache')
          .set('Cross-Origin-Resource-Policy', 'same-origin')
          .set("Permissions-Policy", "accelerometer=(), autoplay=(), camera=(), encrypted-media=(), geolocation=(), gyroscope=(), magnetometer=(), microphone=(), payment=(), speaker=(), sync-xhr=(), usb=() ")
      });
      if (window.location.protocol === 'https:') {
        request = request.clone({
          headers: request.headers
            .set('Strict-Transport-Security', 'max-age=31536000; includeSubDomains; preload'),
        });
      }
    }
    if (this.localStorageService.getDataFromLocalStorage(localStorageVariables.role) === 'Super Admin') {
      if (this.localStorageService.getDataFromLocalStorage(localStorageVariables.agencyId)) {
        request = request.clone({
          headers: request.headers.set(
            'agencyId',
            this.localStorageService.getDataFromLocalStorage(
              localStorageVariables.agencyId
            )
          ),
        });
      }
      if (this.localStorageService.getDataFromLocalStorage(localStorageVariables.tenantAbbreviation)) {
        request = request.clone({
          headers: request.headers.set(
            'tenantId',
            this.localStorageService.getDataFromLocalStorage(
              localStorageVariables.tenantAbbreviation
            )
          ),
        });
      }
    }

    if (!request.headers.has('fileFlag')) {
      if (!request.headers.has('Content-Type')) {
        request = request.clone({
          headers: request.headers.set('Content-Type', 'application/json'),
        });
      }
      request = request.clone({
        headers: request.headers.set('Accept', 'application/json'),
      });
    }
    const agencySharingIndex = this.activatedRoute.snapshot.queryParamMap.get('agencySharingIndex');
    const collaborationSharingIndex = this.activatedRoute.snapshot.queryParamMap.get('collaborationSharingIndex');
    const personIndex = this.activatedRoute.snapshot.queryParamMap.get('personIndex');
    const isActiveForSharing = this.activatedRoute.snapshot.queryParamMap.get('isActiveForSharing');
    if (agencySharingIndex && personIndex && collaborationSharingIndex) {
      if (!request.headers.has('AgencySharingIndex')) {
        request = request.clone({
          headers: request.headers.set('AgencySharingIndex', agencySharingIndex),
        });
      }
      if (!request.headers.has('CollaborationSharingIndex')) {
        request = request.clone({
          headers: request.headers.set('CollaborationSharingIndex', collaborationSharingIndex),
        });
      }
      if (!request.headers.has('personIndex')) {
        request = request.clone({
          headers: request.headers.set('personIndex', personIndex),
        });
      }
      if (!request.headers.has('IsActivePerson') && isActiveForSharing) {
        request = request.clone({
          headers: request.headers.set('IsActivePerson', isActiveForSharing),
        });
      }
    }
    request = request.clone({
      headers: request.headers.set(
        'timeZone',
        new Date().getTimezoneOffset().toString()
      ),
    });

    request = request.clone({
      headers: request.headers.set(
        'X-Content-Type-Options',
        'nosniff'
      ),
    });
    const isEmailAssessmentPage = window.location.pathname.includes('E-ass');
    let culture: string | null = null;
    let agencyLanguageId: string | null = null;
    const cultureMap: { [key: string]: string } = {
      es: 'es-ES',
      it: 'it-CH',
      en: 'en-US',
      'en-workforce': 'en-US',
      'en-safy': 'en-US',
    };
    if (isEmailAssessmentPage) {
      if (this.emailAssessmentLang) {
        const attribute = this.emailAssessmentLang?.attribute || 'en';
        culture = cultureMap[attribute] || null;
        agencyLanguageId = this.emailAssessmentLang?.agencyLanguageID ? String(this.emailAssessmentLang.agencyLanguageID) : null;
      }
    } else {
      const lang = localStorage.getItem('lang') || 'en';
      culture = cultureMap[lang] || null;
      agencyLanguageId = localStorage.getItem('agencyLanguageId');
    }
    if (culture && culture !== 'null') {
      request = request.clone({
        headers: request.headers.set('Culture', culture),
      });
    }
    if (agencyLanguageId && agencyLanguageId !== 'null') {
      request = request.clone({
        headers: request.headers.set('AgencyLanguageID', agencyLanguageId),
      });
    }
    let isfromadvanceSearch = localStorage.getItem('isAdvanceSearchBE');
    if (isfromadvanceSearch && isfromadvanceSearch !== 'null') {
      request = request.clone({
        headers: request.headers.set('isAdvanceSearchBE', isfromadvanceSearch),
      });
    }

    return next.handle(request).pipe(
      tap((evt) => {
        if (evt instanceof HttpResponse) {
          this.interactorService.setErrorFlag(false);
          this.loaderCount--;
          if (this.loaderCount <= 0) {
            this.spinner.hide();
            this.lookupservice.updateOverlayState(false);
          }
          localStorage.setItem('sessionTime', (new Date().getTime()).toString());
          // tslint:disable-next-line: deprecation
          return of(event);
        }
      }),
      catchError((err) => {
        // this.errorLogService.logError(err);
        this.loaderCount--;
        if (this.loaderCount <= 0) {
          this.spinner.hide();
          this.lookupservice.updateOverlayState(false);
        }
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401 && !request.url.includes('refresh-token')) {
            return <any>this.authService.logout(true);
          }
          if (err.status !== 422 && err.status !== 429) {
            this.interactorService.setErrorFlag(true);
          }
          switch ((<HttpErrorResponse>err).status) {
            case 400:
              return throwError(err.error);
            case 403:
              return throwError(err.error);
            case 401:
              return this.handleGlobalError(err);
            case 422: {
              const rawMsg422 = err.error?.error?.exceptionMessage?.responseStatus
                || err.error?.error?.exceptionMessage?.title || 'Unprocessable request.';
              this.interactorService.setErrorFlag(false);
              let toastText422 = rawMsg422;
              this.translatorService.get(rawMsg422).subscribe(translatesText => {
                toastText422 = translatesText;
              });
              this.toastr.error(toastText422, '', { positionClass: 'toast-bottom-right' });
              return throwError(err.error);
            }
            case 429: {
              // Too Many Requests – rate-limit hit.
              if (this.isExcludedFromHttpStatusHandling(request.url)) {
                return throwError(err.error);
              }
              const rawMsg429 = err.error?.error?.exceptionMessage?.responseStatus
                || err.error?.error?.exceptionMessage?.title || 'Too many requests. Please wait a moment and try again.';
              // Suppress the generic error modal and show a targeted toast.
              this.interactorService.setErrorFlag(false);
              let toastText429 = rawMsg429;
              this.translatorService.get(rawMsg429).subscribe(translatesText => {
                toastText429 = translatesText;
              });
              this.toastr.warning(toastText429, '', { positionClass: 'toast-bottom-right' });
              return throwError(err.error);
            }
            case 502:
              return this.handleGlobalError(err);
            case 402:
              return <any>this.authService.logout(true);
            default:
              return throwError(err.error);
          }
        } else {
          return throwError(err);
        }
      })
    );
  }

  /**
   * Handle 401 errors from the API
   * @param request The HTTP request
   * @param next The HTTP handler
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Check if a token refresh is already in progress
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      // Attempt to refresh the token
      return this.authService.refreshToken().pipe(
        switchMap((response: any) => {
          // Handle CallBackTransformService response structure
          const statusCode = response?.StatusCode || response?.statusCode || response?.status;
          const tokenData = response?.Data || response?.data || response?.result || response;

          let accessToken = tokenData?.accessToken || tokenData?.access_token || tokenData?.AccessToken;
          let refreshToken = tokenData?.refreshToken || tokenData?.refresh_token || tokenData?.RefreshToken;

          // Check for successful response and valid access token
          const isSuccessResponse = statusCode === 200 || statusCode === '200';

          if (isSuccessResponse && accessToken) {
            // Store the new access token
            this.localStorageService.saveInLocalStorage(localStorageVariables.token, accessToken);

            // Store the refresh token if provided
            if (refreshToken) {
              this.localStorageService.saveInLocalStorage(localStorageVariables.refreshToken, refreshToken);
            }

            // Let other requests know that token has been refreshed
            this.tokenSubject.next(accessToken);

            // Reset the refresh flag
            this.isRefreshingToken = false;

            // Clone and resend the original request with the new token
            const newRequest = this.addTokenToRequest(request, accessToken);
            return next.handle(newRequest);
          } else {
            // If no valid token in response, logout the user
            this.isRefreshingToken = false;
            this.tokenSubject.next(null);
            this.authService.logout();
            return throwError('Token refresh failed - no valid access token');
          }
        }),
        catchError((error) => {
          this.isRefreshingToken = false;
          this.tokenSubject.next(null);

          // Check if failed due to refresh token expiration or other critical issues
          if (error.status === 400 || error.status === 401) {
            this.authService.logout();
          }

          return throwError(error);
        })
      );
    } else {
      // If a token refresh is already in progress, queue this request
      return this.tokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          // When the token is refreshed, retry the request with the new token
          const newRequest = this.addTokenToRequest(request, token);
          return next.handle(newRequest);
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
    }
  }

  /**
   * Helper method to add the token to a request
   * @param request The original request
   * @param token The token to add
   * @returns A cloned request with the updated token
   */
  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', 'Bearer ' + token)
    });
  }

  logout() {
    this.authService.logout();
  }

  private handleGlobalError(error) {
    this.globalErrorHandlerService.handleError(error)
  }
}
